import { FC, useMemo } from 'react';

import { AtomEffect, atom, useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';

import {
  simpleBugoBrandListDataAtom,
  simpleBugoBrandListStatusAtom,
} from '@shared/api/bugoBrand/bugoBrand.atom';
import { useSimpleBugoBrandListHook } from '@shared/api/bugoBrand/bugoBrand.hook';
import { BugoBrand } from '@shared/api/bugoBrand/bugoBrand.interface';
import {
  giftDeliveryCategoryListDataAtom,
  giftDeliveryCategoryListStatusAtom,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import { GiftDeliveryCategoryData } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.interface';
import {
  giftItemListDataAtom,
  giftItemListStatusAtom,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemListHook } from '@shared/api/giftItem/giftItem.hook';
import { GiftItemData } from '@shared/api/giftItem/giftItem.interface';
import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { Link } from 'react-router-dom';

type SortType =
  | 'order'
  | 'name-asc'
  | 'name-desc'
  | 'price-asc'
  | 'price-desc'
  | 'latest'
  | 'oldest';

// 상품 유형 필터: 전체 / 쿠폰형 / 배송형
type TypeFilter = 'all' | GiftItemInfoType;

// localStorage에 필터 상태를 저장해 페이지 이동/새로고침 후에도 유지
const persistEffect =
  <T,>(key: string): AtomEffect<T> =>
  ({ setSelf, onSet }) => {
    const saved = localStorage.getItem(key);
    if (saved != null) {
      try {
        setSelf(JSON.parse(saved) as T);
      } catch {
        // 손상된 값은 무시
      }
    }
    onSet((newValue, _, isReset) => {
      if (isReset) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(newValue));
      }
    });
  };

const giftItemListBrandFilterAtom = atom<string>({
  key: 'giftItemListBrandFilterAtom',
  default: '',
  effects: [persistEffect<string>('giftItemList.brandFilter')],
});

const giftItemListSearchQueryAtom = atom<string>({
  key: 'giftItemListSearchQueryAtom',
  default: '',
  effects: [persistEffect<string>('giftItemList.searchQuery')],
});

const giftItemListSortTypeAtom = atom<SortType>({
  key: 'giftItemListSortTypeAtom',
  default: 'order',
  effects: [persistEffect<SortType>('giftItemList.sortType')],
});

const giftItemListTypeFilterAtom = atom<TypeFilter>({
  key: 'giftItemListTypeFilterAtom',
  default: 'all',
  effects: [persistEffect<TypeFilter>('giftItemList.typeFilter')],
});

const giftItemListDeliveryCategoryFilterAtom = atom<string>({
  key: 'giftItemListDeliveryCategoryFilterAtom',
  default: '',
  effects: [persistEffect<string>('giftItemList.deliveryCategoryFilter')],
});

export const GiftItemListPage: FC = function GiftItemListPage() {
  const { sellerRole } = useAuth();

  // 필터 및 검색 상태 (localStorage에 저장되어 페이지 이동 후에도 유지됨)
  const [selectedBugoBrandId, setSelectedBugoBrandId] = useRecoilState(
    giftItemListBrandFilterAtom,
  );
  const [searchQuery, setSearchQuery] = useRecoilState(giftItemListSearchQueryAtom);
  const [sortType, setSortType] = useRecoilState(giftItemListSortTypeAtom);
  const [typeFilter, setTypeFilter] = useRecoilState(giftItemListTypeFilterAtom);
  const [selectedDeliveryCategoryId, setSelectedDeliveryCategoryId] = useRecoilState(
    giftItemListDeliveryCategoryFilterAtom,
  );

  // 필터 초기화
  const resetBrandFilter = useResetRecoilState(giftItemListBrandFilterAtom);
  const resetSearchQuery = useResetRecoilState(giftItemListSearchQueryAtom);
  const resetSortType = useResetRecoilState(giftItemListSortTypeAtom);
  const resetTypeFilter = useResetRecoilState(giftItemListTypeFilterAtom);
  const resetDeliveryCategoryFilter = useResetRecoilState(
    giftItemListDeliveryCategoryFilterAtom,
  );

  const resetAllFilters = () => {
    resetBrandFilter();
    resetSearchQuery();
    resetSortType();
    resetTypeFilter();
    resetDeliveryCategoryFilter();
  };

  // 기본값에서 벗어난 필터가 하나라도 있는지
  const hasActiveFilter =
    !!selectedBugoBrandId ||
    !!searchQuery ||
    sortType !== 'order' ||
    typeFilter !== 'all' ||
    !!selectedDeliveryCategoryId;

  // BugoBrand 목록 조회
  useSimpleBugoBrandListHook();
  const bugoBrandListStatus = useRecoilValue(simpleBugoBrandListStatusAtom);
  const bugoBrandList = useRecoilValue(simpleBugoBrandListDataAtom);

  // 배송 카테고리 목록 조회 (배송형 필터의 하위 카테고리 선택용)
  useGiftDeliveryCategoryListHook(sellerRole());
  const deliveryCategoryListStatus = useRecoilValue(giftDeliveryCategoryListStatusAtom);
  const deliveryCategoryList = useRecoilValue(giftDeliveryCategoryListDataAtom);

  // GiftItem 목록 조회
  useGiftItemListHook(sellerRole());

  const status = useRecoilValue(giftItemListStatusAtom);
  const allItemsData = useRecoilValue(giftItemListDataAtom);

  // 선택된 브랜드에 따라 프론트에서 필터링
  const rawData = useMemo(() => {
    if (!allItemsData) return null;
    if (!selectedBugoBrandId) return allItemsData;
    return allItemsData.filter((item) => {
      const brandId =
        typeof item.bugoBrand === 'object' ? item.bugoBrand._id : item.bugoBrand;
      return brandId === selectedBugoBrandId;
    });
  }, [allItemsData, selectedBugoBrandId]);

  const getImageUrl = (item: GiftItemData): string => {
    const image = item.image || item.giftItemInfo?.image;
    if (image && typeof image === 'object' && 'url' in image) {
      return fileBucketUrl((image as Image).url, 'raw');
    }
    return '';
  };

  const getName = (item: GiftItemData): string => {
    return item.name || item.giftItemInfo?.name || '';
  };

  const getBugoBrandName = (item: GiftItemData): string => {
    if (item.bugoBrand && typeof item.bugoBrand === 'object') {
      return item.bugoBrand.bugoAgency?.corpName || item.bugoBrand._id || '-';
    }
    return '-';
  };

  // 표시 순서 가져오기 (GiftItem.order 우선, 없으면 GiftItemInfo.order)
  const getDisplayOrder = (item: GiftItemData): number => {
    if (item.order !== undefined) return item.order;
    if (item.giftItemInfo?.order !== undefined) return item.giftItemInfo.order;
    return Number.MAX_SAFE_INTEGER;
  };

  // 상품 유형 (쿠폰형/배송형) 가져오기 (GiftItemInfo 기준)
  const getItemType = (item: GiftItemData): GiftItemInfoType | undefined => {
    return item.giftItemInfo?.type;
  };

  // 배송 카테고리 ID 가져오기 (GiftItem 오버라이드 우선, 없으면 GiftItemInfo)
  const getDeliveryCategoryId = (item: GiftItemData): string | undefined => {
    const override = item.giftDeliveryCategory;
    if (override) {
      return typeof override === 'object' ? override._id : override;
    }
    const infoCategory = item.giftItemInfo?.giftDeliveryCategory;
    if (infoCategory) {
      return typeof infoCategory === 'object'
        ? (infoCategory as GiftDeliveryCategoryData)._id
        : (infoCategory as unknown as string);
    }
    return undefined;
  };

  // 필터링 및 정렬된 데이터
  const filteredAndSortedData = useMemo(() => {
    if (!rawData) return [];

    let result = [...rawData];

    // 1. 검색 필터
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((item) => {
        const name = getName(item).toLowerCase();
        const brandName = getBugoBrandName(item).toLowerCase();
        return name.includes(query) || brandName.includes(query);
      });
    }

    // 2. 상품 유형 필터 (쿠폰형/배송형)
    if (typeFilter !== 'all') {
      result = result.filter((item) => getItemType(item) === typeFilter);
    }

    // 3. 배송 카테고리 필터 (배송형일 때만)
    if (typeFilter === GiftItemInfoType.Delivery && selectedDeliveryCategoryId) {
      result = result.filter(
        (item) => getDeliveryCategoryId(item) === selectedDeliveryCategoryId,
      );
    }

    // 4. 정렬
    switch (sortType) {
      case 'order':
        result.sort((a, b) => getDisplayOrder(a) - getDisplayOrder(b));
        break;
      case 'name-asc':
        result.sort((a, b) => getName(a).localeCompare(getName(b), 'ko'));
        break;
      case 'name-desc':
        result.sort((a, b) => getName(b).localeCompare(getName(a), 'ko'));
        break;
      case 'price-asc':
        result.sort((a, b) => (a.priceRetail || 0) - (b.priceRetail || 0));
        break;
      case 'price-desc':
        result.sort((a, b) => (b.priceRetail || 0) - (a.priceRetail || 0));
        break;
      case 'latest':
        result.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case 'oldest':
        result.sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
    }

    return result;
  }, [rawData, searchQuery, sortType, typeFilter, selectedDeliveryCategoryId]);

  // 통계 정보
  const stats = useMemo(() => {
    if (!rawData) return { total: 0 };
    return { total: rawData.length };
  }, [rawData]);

  const itemListRender = useMemo(() => {
    if (status === 'success') {
      if (filteredAndSortedData.length === 0) {
        return (
          <div className="py-8 text-center text-gray-500">
            {searchQuery
              ? '검색 결과가 없습니다.'
              : selectedBugoBrandId
              ? '해당 상조에 등록된 상품이 없습니다.'
              : '등록된 상품이 없습니다.'}
          </div>
        );
      }

      return (
        <div className="space-y-2">
          {filteredAndSortedData.map((item) => (
            <Link
              key={item._id}
              to={`/gift-seller/item/${item._id}/edit`}
              className="flex items-center gap-3 rounded-lg border bg-white p-3 hover:bg-gray-50"
            >
              <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                {getImageUrl(item) ? (
                  <img
                    src={getImageUrl(item)}
                    alt={getName(item)}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                    No Image
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  {/* 표시순서 뱃지 */}
                  {(item.order !== undefined ||
                    item.giftItemInfo?.order !== undefined) && (
                    <span
                      className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                        item.order !== undefined
                          ? 'bg-gray-700 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                      title={
                        item.order !== undefined
                          ? '상품 오버라이드 순서'
                          : '원상품 기본 순서'
                      }
                    >
                      #{getDisplayOrder(item)}
                    </span>
                  )}
                  <span className="text-xs font-medium text-indigo-600">
                    {getBugoBrandName(item)}
                  </span>
                </div>
                <p className="truncate font-medium">{getName(item)}</p>
                <div className="flex flex-wrap items-center gap-x-2 text-sm">
                  {item.priceRegular ? (
                    <span className="text-gray-400 line-through">
                      {item.priceRegular?.toLocaleString()}원
                    </span>
                  ) : null}
                  <span className="font-medium text-red-500">
                    {item.priceRetail?.toLocaleString()}원
                  </span>
                  <span className="text-xs text-gray-400">
                    (도매: {item.priceWhole?.toLocaleString()}원)
                  </span>
                  {item.priceRebate ? (
                    <span className="text-xs text-green-600">
                      리베이트: {item.priceRebate?.toLocaleString()}원
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="text-gray-400">→</div>
            </Link>
          ))}
        </div>
      );
    }

    return (
      <div className="center-box h-screen-15">
        <Loading />
      </div>
    );
  }, [status, filteredAndSortedData, searchQuery, selectedBugoBrandId]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">상조별 상품관리</h1>
          <Link to="/gift-seller/item/new">
            <Button className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white">
              + 상품 등록
            </Button>
          </Link>
        </div>

        {/* 통계 */}
        {status === 'success' && (
          <div className="mt-3 flex gap-4 text-sm">
            <span className="text-gray-600">
              전체 <span className="font-medium text-gray-900">{stats.total}</span>개
            </span>
            {selectedBugoBrandId && bugoBrandList && (
              <span className="text-indigo-600">
                {bugoBrandList.find((b: BugoBrand) => b._id === selectedBugoBrandId)
                  ?.bugoAgency?.corpName || selectedBugoBrandId}
              </span>
            )}
          </div>
        )}
      </div>

      {/* 필터 및 검색 */}
      <div className="border-b bg-white px-6 py-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* 검색 */}
          <div className="relative flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="상품명, 상조명 검색..."
              className="w-full rounded-lg border px-3 py-2 pl-9 text-sm"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              🔍
            </span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {/* 상조 필터 */}
          <select
            value={selectedBugoBrandId}
            onChange={(e) => setSelectedBugoBrandId(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
            disabled={bugoBrandListStatus !== 'success'}
          >
            <option value="">전체 상조</option>
            {bugoBrandListStatus === 'success' &&
              bugoBrandList?.map((brand: BugoBrand) => (
                <option key={brand._id} value={brand._id}>
                  {brand.bugoAgency?.corpName || brand._id}
                </option>
              ))}
          </select>

          {/* 상품 유형 필터 (쿠폰형/배송형) */}
          <select
            value={typeFilter}
            onChange={(e) => {
              const value = e.target.value as TypeFilter;
              setTypeFilter(value);
              // 배송형이 아니면 배송 카테고리 필터 초기화
              if (value !== GiftItemInfoType.Delivery) {
                setSelectedDeliveryCategoryId('');
              }
            }}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 유형</option>
            <option value={GiftItemInfoType.Coupon}>쿠폰형</option>
            <option value={GiftItemInfoType.Delivery}>배송형</option>
          </select>

          {/* 배송 카테고리 필터 (배송형 선택 시에만 노출) */}
          {typeFilter === GiftItemInfoType.Delivery && (
            <select
              value={selectedDeliveryCategoryId}
              onChange={(e) => setSelectedDeliveryCategoryId(e.target.value)}
              className="rounded-lg border px-3 py-2 text-sm"
              disabled={deliveryCategoryListStatus !== 'success'}
            >
              <option value="">전체 배송 카테고리</option>
              {deliveryCategoryListStatus === 'success' &&
                deliveryCategoryList?.map((category: GiftDeliveryCategoryData) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
            </select>
          )}

          {/* 정렬 */}
          <select
            value={sortType}
            onChange={(e) => setSortType(e.target.value as SortType)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="order">표시순서</option>
            <option value="latest">최신순</option>
            <option value="oldest">오래된순</option>
            <option value="name-asc">이름 ㄱ→ㅎ</option>
            <option value="name-desc">이름 ㅎ→ㄱ</option>
            <option value="price-asc">가격 낮은순</option>
            <option value="price-desc">가격 높은순</option>
          </select>

          {/* 필터 초기화 */}
          {hasActiveFilter && (
            <button
              onClick={resetAllFilters}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
            >
              필터 초기화
            </button>
          )}
        </div>

        {/* 검색 결과 수 */}
        {status === 'success' && (
          <div className="mt-2 text-xs text-gray-500">
            {filteredAndSortedData.length}개 표시
            {hasActiveFilter && ` (전체 ${allItemsData?.length || 0}개 중)`}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">{itemListRender}</div>
    </div>
  );
};
